import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCarRounded';
import CalculateIcon from '@mui/icons-material/CalculateRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import RestartAltIcon from '@mui/icons-material/RestartAltRounded';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ExpenseAdjustmentsService, {
    ASSET_TYPE,
    USE_VALUE_CALCULATOR_URL,
    USE_VALUE_GROUPS,
    VEHICLE_ADJUSTMENT_TYPES,
    VEHICLE_USAGE,
    monthlyUseValueFromPrice,
} from '../../../Services/expenseAdjustments.service';
import { SALARY_SOURCE } from '../../../Services/salaries.service';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import shell from './AssetModalShell/AssetModalShell.module.scss';
import { xtramInputProps, xtramPopperSx, xtramTextAlign } from './AssetModalShell/assetModalInputs';

export const emptyVehicle = () => ({
    licenseNumber: '',
    manufacturerCode: null,
    manufacturerName: '',
    modelCode: null,
    modelName: '',
    modelYear: null,
    usageKind: VEHICLE_USAGE.PRIVATE,
    adjustmentType: 0,
    noExpenseAdjustment: false,
    useValueGroup: null,
    listPrice: null,
    monthlyUseValue: null,
    monthlyUseValueIsManual: false,
    variableCosts: { sourceType: SALARY_SOURCE.ACCOUNT_CARDS, codes: [] },
    fixedCosts: { sourceType: SALARY_SOURCE.ACCOUNT_CARDS, codes: [] },
    filterByDocumentDate: false,
});

/** The asset's vehicle details as the API expects them (numbers or null). */
export const toVehiclePayload = (assetType, vehicle) => {
    if (assetType !== ASSET_TYPE.VEHICLE) return null;
    const v = { ...emptyVehicle(), ...(vehicle || {}) };
    const num = (value) => (value === '' || value === null || value === undefined || Number.isNaN(Number(value)) ? null : Number(value));
    return {
        ...v,
        manufacturerCode: num(v.manufacturerCode),
        modelCode: num(v.modelCode),
        modelYear: num(v.modelYear),
        useValueGroup: num(v.useValueGroup),
        listPrice: num(v.listPrice),
        monthlyUseValue: num(v.monthlyUseValue),
    };
};

const hasValue = (value) => value !== null && value !== undefined && value !== '';
const roundCents = (value) => Math.round(value * 100) / 100;
// The monthly value is kept in agorot, so a typed yearly amount comes back within a few agorot: show whole shekels.
const yearlyOf = (monthly) => (hasValue(monthly) && Number.isFinite(Number(monthly)) ? Math.round(Number(monthly) * 12) : null);
const modelLabel = (model) => model.commercialName || model.modelName || String(model.modelCode);

/** Loads a catalog list whenever `key` changes; `load` returns null when there is nothing to load. */
const useCatalogList = (key, load) => {
    const [state, setState] = useState({ items: [], loading: false, failed: false });
    useEffect(() => {
        const request = load();
        if (!request) {
            setState({ items: [], loading: false, failed: false });
            return undefined;
        }
        let active = true;
        setState((prev) => ({ ...prev, loading: true, failed: false }));
        request
            .then((items) => active && setState({ items: items || [], loading: false, failed: false }))
            .catch((error) => {
                console.error('Vehicle catalog lookup failed:', error);
                if (active) setState({ items: [], loading: false, failed: true });
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    return state;
};

const AssetVehicleSection = ({ assetType, vehicle, onChange, token, direction, taxYear }) => {
    const { t } = useTranslation();
    const TAX_YEAR = Number(taxYear) > 0 ? Number(taxYear) : new Date().getFullYear();
    const [loading, setLoading] = useState(false);
    const [priceOptions, setPriceOptions] = useState([]);
    const [yearValue, setYearValue] = useState(null);
    // What the accountant is typing in the yearly field; the stored monthly value is rounded to agorot.
    const [yearlyDraft, setYearlyDraft] = useState(null);
    const isVehicle = assetType === ASSET_TYPE.VEHICLE;
    const current = { ...emptyVehicle(), ...(vehicle || {}) };
    const isCommercial = Number(current.usageKind) === VEHICLE_USAGE.COMMERCIAL;
    const latest = useRef(current);
    latest.current = current;
    const monthly = hasValue(current.monthlyUseValue) ? Number(current.monthlyUseValue) : null;

    const useValueInputs = isVehicle && !isCommercial
        ? JSON.stringify([current.listPrice, current.modelYear, current.useValueGroup, TAX_YEAR])
        : null;

    // The use value of the report's tax year: the price-group amount, or the list price linked by the index from the
    // registration year and capped. A manual value is kept as typed.
    useEffect(() => {
        if (!useValueInputs) return undefined;
        let active = true;
        const timer = setTimeout(async () => {
            const { listPrice, modelYear: year, useValueGroup } = latest.current;
            try {
                const result = await ExpenseAdjustmentsService.getVehicleUseValueForYear({
                    taxYear: TAX_YEAR,
                    listPrice,
                    registrationYear: year,
                    useValueGroup,
                }, token);
                if (!active) return;
                setYearValue(result);
                const now = latest.current;
                if (result.basis !== 'none' && !now.monthlyUseValueIsManual
                    && Number(now.monthlyUseValue) !== Number(result.monthlyUseValue)) {
                    onChange(ASSET_TYPE.VEHICLE, { ...now, monthlyUseValue: result.monthlyUseValue });
                }
            } catch (error) {
                console.error('Use value for the tax year failed:', error);
                if (active) setYearValue(null);
            }
        }, 350);
        return () => {
            active = false;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useValueInputs, token]);

    const manufacturerCode = hasValue(current.manufacturerCode) ? Number(current.manufacturerCode) : null;
    const modelYear = hasValue(current.modelYear) ? Number(current.modelYear) : null;

    const manufacturers = useCatalogList(
        isVehicle ? 'manufacturers' : 'off',
        () => (isVehicle ? ExpenseAdjustmentsService.getVehicleManufacturers(token) : null),
    );
    const years = useCatalogList(
        isVehicle && manufacturerCode ? `years:${manufacturerCode}` : 'off',
        () => (isVehicle && manufacturerCode ? ExpenseAdjustmentsService.getVehicleModelYears(manufacturerCode, token) : null),
    );
    const models = useCatalogList(
        isVehicle && manufacturerCode && modelYear ? `models:${manufacturerCode}:${modelYear}` : 'off',
        () => (isVehicle && manufacturerCode && modelYear
            ? ExpenseAdjustmentsService.getVehicleModels(manufacturerCode, modelYear, token)
            : null),
    );
    // Without the price list (offline / data.gov.il down) the codes are typed as before.
    const catalogUnavailable = manufacturers.failed;

    const inputProps = xtramInputProps(direction);
    const inputStyle = xtramTextAlign(direction);
    const ltrStyle = xtramTextAlign('ltr');
    const set = (patch) => onChange(ASSET_TYPE.VEHICLE, { ...current, ...patch });

    const label = (text) => (
        <span className={shell.fieldLabel}><span>{text}</span></span>
    );

    const field = (key, text, { type = 'text', ltr = false, onValue, value, helperText, onBlur } = {}) => (
        <div className={shell.field}>
            {label(text)}
            <TextField
                fullWidth
                variant="standard"
                type={type}
                value={value ?? current[key] ?? ''}
                onChange={(e) => (onValue ? onValue(e.target.value) : set({ [key]: e.target.value }))}
                onBlur={onBlur}
                InputProps={inputProps}
                inputProps={ltr ? ltrStyle : inputStyle}
                helperText={helperText}
            />
        </div>
    );

    const picker = ({ text, options, value, onPick, getOptionLabel, renderOption, loadingList, disabled, placeholder, ltr = false, wide = false, isEqual }) => (
        <div className={shell.field} style={wide ? { gridColumn: '1 / -1' } : undefined}>
            {label(text)}
            <Autocomplete
                fullWidth
                options={options}
                value={value}
                onChange={(_, next) => onPick(next)}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isEqual || ((option, selected) => getOptionLabel(option) === getOptionLabel(selected))}
                renderOption={renderOption}
                loading={loadingList}
                loadingText={t('Loading')}
                noOptionsText={t('NoOptions')}
                disabled={disabled}
                componentsProps={{ popper: { sx: xtramPopperSx } }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="standard"
                        placeholder={placeholder}
                        InputProps={{ ...params.InputProps, ...inputProps }}
                        inputProps={{ ...params.inputProps, ...(ltr ? ltrStyle : inputStyle) }}
                    />
                )}
            />
        </div>
    );

    const selectedManufacturer = useMemo(() => {
        if (!manufacturerCode) return null;
        return manufacturers.items.find((m) => m.code === manufacturerCode)
            || { code: manufacturerCode, name: current.manufacturerName || String(manufacturerCode) };
    }, [manufacturerCode, manufacturers.items, current.manufacturerName]);

    const yearOptions = useMemo(
        () => (modelYear && !years.items.includes(modelYear) ? [modelYear, ...years.items] : years.items),
        [modelYear, years.items],
    );

    const selectedModel = useMemo(() => {
        const code = hasValue(current.modelCode) ? Number(current.modelCode) : null;
        if (!code) return null;
        const sameCode = models.items.filter((m) => m.modelCode === code);
        return sameCode.find((m) => Number(m.price) === Number(current.listPrice))
            || sameCode[0]
            || { modelCode: code, modelName: current.modelName, commercialName: current.modelName, price: current.listPrice };
    }, [current.modelCode, current.modelName, current.listPrice, models.items]);

    const priceUpdate = (price) => (current.monthlyUseValueIsManual
        ? {}
        : { monthlyUseValue: monthlyUseValueFromPrice(price) });

    const pickManufacturer = (option) => set({
        manufacturerCode: option?.code ?? null,
        manufacturerName: option?.name || '',
        modelYear: null,
        modelCode: null,
        modelName: '',
    });

    const pickYear = (year) => set({ modelYear: year ?? null, modelCode: null, modelName: '' });

    const pickModel = (option) => {
        if (!option) {
            set({ modelCode: null, modelName: '' });
            return;
        }
        set({
            modelCode: option.modelCode,
            modelName: modelLabel(option),
            listPrice: option.price,
            monthlyUseValue: option.monthlyUseValue ?? monthlyUseValueFromPrice(option.price),
            monthlyUseValueIsManual: false,
            usageKind: option.modelType === 'M' ? VEHICLE_USAGE.COMMERCIAL : current.usageKind,
        });
    };

    const applyPrice = (option) => set({
        listPrice: option.price,
        monthlyUseValue: option.monthlyUseValue,
        monthlyUseValueIsManual: false,
        manufacturerName: current.manufacturerName || option.manufacturerName || '',
        modelName: current.modelName || option.commercialName || option.modelName || '',
    });

    const resetUseValue = () => set({
        monthlyUseValue: yearValue && yearValue.basis !== 'none'
            ? yearValue.monthlyUseValue
            : monthlyUseValueFromPrice(current.listPrice),
        monthlyUseValueIsManual: false,
    });

    const groupAmount = (group) => yearValue?.groups?.find((g) => g.group === group)?.monthlyUseValue;

    const describeUseValue = () => {
        if (current.monthlyUseValueIsManual) {
            return monthly !== null ? t('VehicleMonthlyUseValueHint', { value: beautifyNumber(monthly) }) : '';
        }
        if (yearValue?.basis === 'group') {
            return t('VehicleUseValueByGroupHint', {
                group: current.useValueGroup,
                monthly: beautifyNumber(yearValue.monthlyUseValue),
                year: yearValue.taxYear,
            });
        }
        if (yearValue?.basis === 'listPrice') {
            return [
                t('VehicleAdjustedPriceHint', { year: yearValue.taxYear, price: beautifyNumber(yearValue.adjustedListPrice) }),
                yearValue.ceilingApplied ? t('VehicleCeilingApplied') : null,
                !yearValue.indexAvailable ? t('VehicleIndexUnavailable') : null,
                t('VehicleMonthlyUseValueHint', { value: beautifyNumber(yearValue.monthlyUseValue) }),
            ].filter(Boolean).join(' · ');
        }
        return t('VehicleUseValueAutoHint');
    };

    const handleFetch = async () => {
        if (!current.licenseNumber && !(current.manufacturerCode && current.modelCode && current.modelYear)) {
            toast.info(t('VehicleLookupNeedsDetails'));
            return;
        }
        setLoading(true);
        try {
            const result = await ExpenseAdjustmentsService.getVehicleUseValue(current, token);
            const found = result?.vehicle;
            const next = { ...current };
            if (found) {
                next.manufacturerCode = found.manufacturerCode ?? next.manufacturerCode;
                next.manufacturerName = found.manufacturerName || next.manufacturerName;
                next.modelCode = found.modelCode ?? next.modelCode;
                next.modelName = found.commercialName || found.modelName || next.modelName;
                next.modelYear = found.modelYear ?? next.modelYear;
                next.usageKind = found.modelType === 'M' ? VEHICLE_USAGE.COMMERCIAL : next.usageKind;
            }
            setPriceOptions(result?.options || []);
            if (result?.found) {
                next.listPrice = result.listPrice;
                next.monthlyUseValue = result.monthlyUseValue;
                next.monthlyUseValueIsManual = false;
                toast.success(t('VehicleUseValueFetched', { value: beautifyNumber(yearlyOf(result.monthlyUseValue)) }));
            } else {
                toast.info(t('VehicleUseValueNotFound'));
            }
            onChange(ASSET_TYPE.VEHICLE, next);
        } catch (error) {
            console.error('Vehicle use value lookup failed:', error);
            toast.error(t('VehicleLookupFailed'));
        } finally {
            setLoading(false);
        }
    };

    const catalogFields = (
        <>
            {picker({
                text: t('VehicleManufacturer'),
                options: manufacturers.items,
                value: selectedManufacturer,
                onPick: pickManufacturer,
                getOptionLabel: (option) => option?.name || '',
                isEqual: (option, selected) => option.code === selected.code,
                renderOption: (props, option) => (
                    <li {...props} key={props.id}>
                        <span style={{ flex: 1 }}>{option.name}</span>
                        <span style={{ opacity: 0.55, direction: 'ltr' }}>{option.code}</span>
                    </li>
                ),
                loadingList: manufacturers.loading,
                placeholder: t('VehicleSelectManufacturer'),
            })}
            {picker({
                text: t('VehicleModelYear'),
                options: yearOptions,
                value: modelYear,
                onPick: pickYear,
                getOptionLabel: (option) => (option === null || option === undefined ? '' : String(option)),
                loadingList: years.loading,
                disabled: !manufacturerCode,
                placeholder: t('VehicleSelectYear'),
                ltr: true,
            })}
            {picker({
                text: t('VehicleModel'),
                options: models.items,
                value: selectedModel,
                onPick: pickModel,
                getOptionLabel: (option) => (option ? `${modelLabel(option)} (${option.modelCode})` : ''),
                isEqual: (option, selected) => option.modelCode === selected.modelCode
                    && Number(option.price) === Number(selected.price),
                renderOption: (props, option) => (
                    <li {...props} key={props.id}>
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <span>{`${modelLabel(option)} (${option.modelCode})`}</span>
                            <span style={{ fontSize: 11, opacity: 0.65 }}>
                                {[
                                    option.commercialName && option.modelName !== option.commercialName ? option.modelName : null,
                                    option.importerName,
                                    `${t('VehicleListPrice')} ${beautifyNumber(option.price)} ₪`,
                                ].filter(Boolean).join(' · ')}
                            </span>
                        </div>
                    </li>
                ),
                loadingList: models.loading,
                disabled: !manufacturerCode || !modelYear,
                placeholder: t('VehicleSelectModel'),
                wide: true,
            })}
        </>
    );

    const typedFields = (
        <>
            {field('manufacturerCode', t('VehicleManufacturerCode'), { type: 'number', ltr: true })}
            {field('modelCode', t('VehicleModelCode'), { type: 'number', ltr: true })}
            {field('modelYear', t('VehicleModelYear'), { type: 'number', ltr: true })}
            {field('modelName', t('VehicleModelName'))}
        </>
    );

    return (
        <section className={shell.section}>
            <h3 className={shell.sectionTitle}>
                <DirectionsCarIcon sx={{ fontSize: 18 }} />
                {t('AssetTypeAndVehicle')}
            </h3>
            <div className={shell.fieldGrid}>
                <div className={shell.field}>
                    {label(t('AssetType'))}
                    <TextField
                        select
                        fullWidth
                        variant="standard"
                        value={assetType ?? ASSET_TYPE.GENERAL}
                        onChange={(e) => {
                            const next = Number(e.target.value);
                            onChange(next, next === ASSET_TYPE.VEHICLE ? current : vehicle);
                        }}
                        InputProps={inputProps}
                    >
                        <MenuItem value={ASSET_TYPE.GENERAL}>{t('AssetTypeGeneral')}</MenuItem>
                        <MenuItem value={ASSET_TYPE.VEHICLE}>{t('AssetTypeVehicle')}</MenuItem>
                    </TextField>
                </div>
                {isVehicle ? field('licenseNumber', t('LicenseNumber'), { ltr: true }) : null}
            </div>

            {isVehicle ? (
                <>
                    <div className={shell.fieldGrid} style={{ marginTop: 10 }}>
                        {catalogUnavailable ? typedFields : catalogFields}
                        <div className={shell.field}>
                            {label(t('VehicleUsageKind'))}
                            <TextField
                                select
                                fullWidth
                                variant="standard"
                                value={current.usageKind}
                                onChange={(e) => set({ usageKind: Number(e.target.value) })}
                                InputProps={inputProps}
                            >
                                <MenuItem value={VEHICLE_USAGE.PRIVATE}>{t('VehicleUsagePrivate')}</MenuItem>
                                <MenuItem value={VEHICLE_USAGE.COMMERCIAL}>{t('VehicleUsageCommercial')}</MenuItem>
                            </TextField>
                        </div>
                        {isCommercial ? (
                            <div className={shell.field} style={{ gridColumn: '1 / -1' }}>
                                <span className={shell.notice}>{t('VehicleCommercialNoAdjustment')}</span>
                            </div>
                        ) : null}
                        {!isCommercial ? (
                        <div className={shell.field}>
                            {label(t('VehicleAdjustmentType'))}
                            <TextField
                                select
                                fullWidth
                                variant="standard"
                                value={current.adjustmentType}
                                onChange={(e) => set({ adjustmentType: Number(e.target.value) })}
                                InputProps={inputProps}
                            >
                                {VEHICLE_ADJUSTMENT_TYPES.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {`${t(type.labelKey)} (${type.percentage}%)`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                        ) : null}
                        {field('listPrice', t('VehicleListPrice'), {
                            type: 'number',
                            ltr: true,
                            onValue: (value) => set({ listPrice: value, ...priceUpdate(value) }),
                        })}
                        {!isCommercial ? (
                        <>
                        {field('yearlyUseValue', t('VehicleYearlyUseValueForYear', { year: TAX_YEAR }), {
                            type: 'number',
                            ltr: true,
                            value: yearlyDraft ?? yearlyOf(current.monthlyUseValue) ?? '',
                            onValue: (value) => {
                                setYearlyDraft(value);
                                set({
                                    monthlyUseValue: value === '' || !Number.isFinite(Number(value))
                                        ? null
                                        : roundCents(Number(value) / 12),
                                    monthlyUseValueIsManual: value !== '',
                                });
                            },
                            onBlur: () => setYearlyDraft(null),
                            helperText: describeUseValue(),
                        })}
                        <div className={shell.field}>
                            {label(t('VehicleUseValueGroup'))}
                            <TextField
                                select
                                fullWidth
                                variant="standard"
                                value={hasValue(current.useValueGroup) ? Number(current.useValueGroup) : ''}
                                onChange={(e) => set({
                                    useValueGroup: e.target.value === '' ? null : Number(e.target.value),
                                    monthlyUseValueIsManual: false,
                                })}
                                InputProps={inputProps}
                                SelectProps={{ displayEmpty: true }}
                                helperText={t('VehicleUseValueGroupHint')}
                            >
                                <MenuItem value="">{t('VehicleUseValueGroupByPrice')}</MenuItem>
                                {USE_VALUE_GROUPS.map((group) => {
                                    const amount = groupAmount(group);
                                    return (
                                        <MenuItem key={group} value={group}>
                                            {amount
                                                ? t('VehicleUseValueGroupAmount', {
                                                    group,
                                                    monthly: beautifyNumber(amount),
                                                    yearly: beautifyNumber(amount * 12),
                                                })
                                                : t('VehicleUseValueGroupN', { group })}
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        </div>
                        <div className={shell.field}>
                            {label(' ')}
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        size="small"
                                        checked={!!current.noExpenseAdjustment}
                                        onChange={(e) => set({ noExpenseAdjustment: e.target.checked })}
                                    />
                                )}
                                label={t('VehicleNoExpenseAdjustment')}
                            />
                        </div>
                        </>
                        ) : null}
                    </div>

                    {!isCommercial ? (
                    <div className={shell.toolbar} style={{ marginTop: 10 }}>
                        <button type="button" className={`${shell.blueBtn} ${shell.small}`} onClick={handleFetch} disabled={loading}>
                            <CalculateIcon />
                            {loading ? t('Loading') : t('VehicleFetchUseValue')}
                        </button>
                        <a
                            className={`${shell.secondaryBtn} ${shell.small}`}
                            href={USE_VALUE_CALCULATOR_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <OpenInNewIcon />
                            {t('VehicleUseValueCalculator')}
                        </a>
                        {current.monthlyUseValueIsManual ? (
                            <>
                                <span className={shell.chip}>{t('VehicleUseValueManual')}</span>
                                {hasValue(current.listPrice) || hasValue(current.useValueGroup) ? (
                                    <button type="button" className={`${shell.secondaryBtn} ${shell.small}`} onClick={resetUseValue}>
                                        <RestartAltIcon />
                                        {t('VehicleUseValueRecalculate')}
                                    </button>
                                ) : null}
                            </>
                        ) : null}
                    </div>
                    ) : null}

                    {!isCommercial && priceOptions.length > 1 ? (
                        <div className={shell.toolbar} style={{ marginTop: 8, flexWrap: 'wrap' }}>
                            {priceOptions.map((option, index) => (
                                <button
                                    key={`${option.importerName}-${option.modelName}-${index}`}
                                    type="button"
                                    className={`${shell.secondaryBtn} ${shell.small}`}
                                    onClick={() => applyPrice(option)}
                                >
                                    {`${option.commercialName || option.modelName || ''} · ${beautifyNumber(option.price)} ₪ → ${beautifyNumber(yearlyOf(option.monthlyUseValue))} ₪`}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </>
            ) : null}
        </section>
    );
};

AssetVehicleSection.propTypes = {
    assetType: PropTypes.number,
    vehicle: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    token: PropTypes.string,
    direction: PropTypes.string,
    /** The tax year the use value is calculated for (the report year); defaults to the current year. */
    taxYear: PropTypes.number,
};

export default AssetVehicleSection;
